import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { Badge } from "@bednbody/ui/components/badge";
import { sessionStateLabel, therapistOutcomeLabel } from "@bednbody/logic";
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import type { useAttendance } from "../ops-dashboard/ops-dashboard.hooks";
import { OUTCOME_VARIANT, STATE_VARIANT } from "./attendance-tone";

export type AttendanceRow = NonNullable<ReturnType<typeof useAttendance>["data"]>[number];

/** How long they have been on session, from the recorded start. */
function elapsed(startedIso: string | null): string | null {
  if (!startedIso) return null;
  const mins = Math.floor((Date.now() - new Date(startedIso).getTime()) / 60_000);
  if (mins < 0) return null;
  const h = Math.floor(mins / 60);
  return h > 0 ? `${h}h ${mins % 60}m` : `${mins}m`;
}

const time = (iso: string | null) =>
  iso ? new Date(iso).toLocaleTimeString("en-PH", { hour: "numeric", minute: "2-digit" }) : null;

/**
 * Read-only detail view. Deliberately NO "force start" or "edit session"
 * actions: a Session is started only by the staff member themselves
 * (`start_therapist_session` / `start_rider_session` resolve `auth.uid()`) and
 * no attendance correction authority is defined in the vault — an Open Item,
 * not a button. The defined fix path is the BOOKING session start on /bookings
 * (UX Notes §Hub Manager), and the admin session override on /therapist-sessions.
 *
 * ⚠ IT SHOWS THE STATUS AND THE TIMES, AND NOTHING ELSE. TEND-068 caps what
 * may be shown about an attendance outcome at exactly that — never the internal
 * reason, the evidence, or the comparison that produced it. The cap is written
 * about the therapist's own History, and honouring it here too costs nothing
 * and keeps one rule instead of two.
 *
 * ⚠ WAS A HAND-ROLLED `fixed inset-0` OVERLAY. `frontend-standards.md` §6
 * forbids those outright, and not on taste: the hand-rolled version appeared
 * and vanished with no transition, closed only on backdrop click or its own
 * button, and trapped no focus. The package `Dialog` brings the enter/exit
 * animation, Escape, focus trap and `aria-modal` with it.
 */
export function SessionDetailModal({
  staff,
  onClose,
}: {
  staff: AttendanceRow | null;
  onClose: () => void;
}) {
  // ⚠ THE LAST NON-NULL ROW, KEPT FOR THE EXIT ANIMATION. The parent nulls
  // `staff` to close, so rendering straight from the prop would empty the
  // dialog while it is still fading out — the panel collapses to nothing and
  // then disappears, which reads worse than the abrupt version this replaced.
  const shown = useRef<AttendanceRow | null>(staff);
  useEffect(() => {
    if (staff) shown.current = staff;
  }, [staff]);
  const row = staff ?? shown.current;
  const a = row?.attendance ?? null;
  const onSession = a ? elapsed(row?.startedAtIso ?? null) : null;

  return (
    <Dialog open={staff !== null} onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent>
        {row ? (
          <>
            <DialogHeader>
              <DialogTitle>{row.name}</DialogTitle>
              <DialogDescription className="capitalize">
                {row.role}
                {row.hubName ? ` · ${row.hubName}` : ""}
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-wrap items-center gap-2">
              {a ? (
                <Badge variant={STATE_VARIANT[a.state]}>{sessionStateLabel(a.state)}</Badge>
              ) : (
                <Badge variant="muted">Not scheduled</Badge>
              )}
              {a?.outcome ? (
                <Badge variant={OUTCOME_VARIANT[a.outcome]}>
                  {therapistOutcomeLabel(a.outcome)}
                  {a.outcomeIsRecorded ? "" : " (pending)"}
                </Badge>
              ) : a?.lateStart ? (
                <Badge variant="warning">Late start</Badge>
              ) : a?.runningLate ? (
                <Badge variant="warning">Running late</Badge>
              ) : null}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-border bg-background p-3">
                <p className="text-xs text-muted-foreground">Scheduled</p>
                <p className="font-medium tabular-nums">
                  {row.startsAt && row.endsAt
                    ? `${row.sessionCode ? `${row.sessionCode} · ` : ""}${time(row.startsAt)}–${time(row.endsAt)}`
                    : "-"}
                </p>
              </div>
              <div className="rounded-lg border border-border bg-background p-3">
                <p className="text-xs text-muted-foreground">Started</p>
                <p className="font-medium tabular-nums">{row.startedTime ?? "-"}</p>
              </div>
              <div className="rounded-lg border border-border bg-background p-3">
                <p className="text-xs text-muted-foreground">Ended</p>
                <p className="font-medium tabular-nums">{row.endedTime ?? "-"}</p>
              </div>
              {a?.onDuty && onSession ? (
                <div className="rounded-lg border border-border bg-background p-3">
                  <p className="text-xs text-muted-foreground">On session for</p>
                  <p className="font-medium tabular-nums">{onSession}</p>
                </div>
              ) : null}
              <div className="rounded-lg border border-border bg-background p-3">
                <p className="text-xs text-muted-foreground">Current booking</p>
                <p className="font-medium">{row.currentBooking ?? "None"}</p>
              </div>
            </div>

            {row.currentBooking ? (
              <Link
                to="/bookings"
                className="block rounded-lg border border-border px-3 py-2 text-center text-sm font-medium text-primary hover:bg-background"
              >
                Manage {row.currentBooking} on Bookings
              </Link>
            ) : null}
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
